import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  SelectChangeEvent,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ChangeEvent, FC, memo, useEffect, useState } from 'react';
import { SalesOrderRecurrenceRule } from 'src/entities/SalesOrder.response.entity';
import { formatRecurrence, isEqual } from 'src/utils/recurrenceFormatter';

type CustomRepeatDialogProps = {
  currentRule: SalesOrderRecurrenceRule;
  open: boolean;
  onClose: () => void;
  onSave: (settings: SalesOrderRecurrenceRule) => void;
  // date: Date;
};

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// カスタム繰り返し設定用のダイアログ
const CustomRepeatDialog: FC<CustomRepeatDialogProps> = memo(({
  currentRule,
  open,
  onClose,
  onSave,
  // date
}) => {
  // 繰り返し間隔
  const [interval, setInterval] = useState(1);
  // 単位: 日ごと(daily) / 週ごと(weekly) / 月ごと(monthly)
  const [frequency, setFrequency] = useState<'none' | 'daily' | 'weekly' | 'monthly'>('daily');

  // 週ごとの曜日チェック
  const [dayOfWeeks, setDayOfWeeks] = useState({
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
    sun: false,
  });
  // 月ごとの指定方法: "dayOfMonth" (毎月○日) or "nthWeekday" (毎月 第n○曜日)
  const [recurrenceType, setRecurrenceType] = useState<'day_of_month' | 'weekday_of_month'>('day_of_month');
  // 毎月○日
  const [dayOfMonth, setDayOfMonth] = useState(1);
  // 毎月 第n○曜日
  const [weekOfMonth, setWeekOfMonth] = useState(1); // 第2
  const [weekdayOfMonth, setWeekdayOfMonth] = useState('mon');

  const [endCondition, setEndCondition] = useState<'none' | 'by_date' | 'by_count'>('none');
  const [endDate, setEndDate] = useState(new Date());
  const [occurrences, setOccurrences] = useState<number>(1);

  useEffect(() => {
    if (!currentRule) return;

    if (currentRule.interval) setInterval(currentRule.interval);
    if (currentRule.frequency) setFrequency(currentRule.frequency);
    if (currentRule.dayOfWeeks) setDayOfWeeks(currentRule.dayOfWeeks);
    if (currentRule.recurrenceType) setRecurrenceType(currentRule.recurrenceType);
    if (currentRule.dayOfMonth) setDayOfMonth(currentRule.dayOfMonth);
    if (currentRule.weekOfMonth) setWeekOfMonth(currentRule.weekOfMonth);
    if (currentRule.frequency === 'monthly' && currentRule.recurrenceType === 'weekday_of_month') {
      WEEKDAYS.forEach((it) => {
        if (currentRule.dayOfWeeks[it]) setWeekdayOfMonth(it);
      });
    }
    if (currentRule.endCondition) setEndCondition(currentRule.endCondition);
    if (currentRule.endDate) setEndDate(currentRule.endDate);
    if (currentRule.occurrences) setOccurrences(currentRule.occurrences);
  }, [currentRule]);

  useEffect(() => {
    setDayOfWeeks({
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false,
      [weekdayOfMonth]: true,
    });
  }, [weekdayOfMonth]);

  // 週ごとの曜日チェック変更
  const handleWeeklyDaysChange = (event:ChangeEvent<HTMLInputElement>) => {
    setDayOfWeeks({
      ...dayOfWeeks,
      [event.target.name]: event.target.checked,
    });
  };

  // 保存ボタン押下時
  const handleSave = () => {
    // カスタム設定をまとめて親コンポーネントに渡す
    const customSettings: SalesOrderRecurrenceRule = {
      interval,
      frequency,
      recurrenceType,
      weekOfMonth,
      dayOfWeeks,
      dayOfMonth,
      endCondition,
      endDate,
      occurrences,
    };
    onSave(customSettings);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>カスタムの繰り返し</DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        {/* 繰り返し間隔 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
          <TextField
            label="繰り返し間隔"
            type="number"
            variant="outlined"
            size="small"
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            sx={{ width: 100, mr: 1 }}
          />
          <FormControl size="small">
            <Select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
            >
              <MenuItem value="daily">日ごと</MenuItem>
              <MenuItem value="weekly">週ごと</MenuItem>
              <MenuItem value="monthly">月ごと</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* 単位ごとの追加設定 */}
        {frequency === 'weekly' && (
          <Box sx={{ mb: 2 }}>
            <Box>曜日:</Box>
            <Stack direction="row">
              {Object.entries(dayOfWeeks).sort((a, b) => WEEKDAYS.indexOf(a[0]) - WEEKDAYS.indexOf(b[0])).map(([key, value]) => {
                const labelMap = {
                  mon: '月',
                  tue: '火',
                  wed: '水',
                  thu: '木',
                  fri: '金',
                  sat: '土',
                  sun: '日',
                };
                return (
                  <FormControlLabel
                    key={key}
                    control={(
                      <Checkbox
                        name={key}
                        checked={value}
                        onChange={handleWeeklyDaysChange}
                      />
                    )}
                    label={labelMap[key] as string}
                  />
                );
              })}
            </Stack>
          </Box>
        )}

        {frequency === 'monthly' && (
          <Box sx={{ mb: 2 }}>
            {/* 毎月の指定方法を切り替える */}
            <FormControl size="small" sx={{ mb: 1 }}>
              <InputLabel id="monthly-type-label">月ごとの設定</InputLabel>
              <Stack direction="row" alignItems="baseline">
                <Select
                  labelId="monthly-type-label"
                  value={recurrenceType}
                  label="月ごとの設定"
                  onChange={(e) => setRecurrenceType(e.target.value)}
                >
                  <MenuItem value="day_of_month">日付指定</MenuItem>
                  <MenuItem value="weekday_of_month">曜日指定</MenuItem>
                </Select>
                <Stack sx={{ ml: 2 }}>
                  {recurrenceType === 'day_of_month' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <TextField
                        label="日"
                        type="number"
                        size="small"
                        value={dayOfMonth}
                        onChange={(e) => setDayOfMonth(Number(e.target.value))}
                        sx={{ width: 100, mr: 1 }}
                      />
                      <span>日</span>
                    </Box>
                  )}

                  {recurrenceType === 'weekday_of_month' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <FormControl size="small" sx={{ mr: 1 }}>
                        <Select
                          value={weekOfMonth}
                          onChange={(e) => setWeekOfMonth(Number(e.target.value))}
                        >
                          <MenuItem value={1}>第1</MenuItem>
                          <MenuItem value={2}>第2</MenuItem>
                          <MenuItem value={3}>第3</MenuItem>
                          <MenuItem value={4}>第4</MenuItem>
                          <MenuItem value={5}>第5(最終)</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small">
                        <Select
                          value={weekdayOfMonth}
                          onChange={(e) => setWeekdayOfMonth(e.target.value)}
                        >
                          <MenuItem value="mon">月曜日</MenuItem>
                          <MenuItem value="tue">火曜日</MenuItem>
                          <MenuItem value="wed">水曜日</MenuItem>
                          <MenuItem value="thu">木曜日</MenuItem>
                          <MenuItem value="fri">金曜日</MenuItem>
                          <MenuItem value="sat">土曜日</MenuItem>
                          <MenuItem value="sun">日曜日</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </FormControl>
          </Box>
        )}

        {/* 終了条件 */}
        <Box sx={{ mb: 2 }}>
          <Box>終了日:</Box>
          <RadioGroup
            value={endCondition}
            onChange={(e) => setEndCondition(e.target.value as 'none' | 'by_date' | 'by_count')}
          >
            <FormControlLabel value="none" control={<Radio />} label="なし" />
            <Stack direction="row">
              <FormControlLabel value="by_date" control={<Radio />} label="終了日: " />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="終了日を選択"
                  format="yyyy/MM/dd"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  disabled={endCondition !== 'by_date'}
                  slotProps={{
                    textField: {
                      size: "small",
                      sx: { width: '11em', mt: 1 }
                    }
                  }}
                />
              </LocalizationProvider>
            </Stack>
            <Stack direction="row">
              <FormControlLabel value="by_count" control={<Radio />} label="繰り返し: " />
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TextField
                  label="回数"
                  type="number"
                  size="small"
                  value={occurrences}
                  disabled={endCondition !== 'by_count'}
                  onChange={(e) => setOccurrences(Number(e.target.value) > 0 ? Number(e.target.value) : undefined)}
                  sx={{ width: 100, mr: 1 }}
                />
                <span>回</span>
              </Box>
            </Stack>
          </RadioGroup>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" onClick={handleSave}>完了</Button>
      </DialogActions>
    </Dialog>
  );
});

export type RepeatSelectProps = {
  currentRule: SalesOrderRecurrenceRule;
  handleSave: (rule: SalesOrderRecurrenceRule) => void;
//  date: Date;
};

const RepeatSelect: FC<RepeatSelectProps> = memo(({
  currentRule,
  handleSave,
//  date,
}) => {
  const [repeatType, setRepeatType] = useState('none');
  const [repeatTypeForRestore, setRepeatTypeForRestore] = useState('none');
  const [openCustomDialog, setOpenCustomDialog] = useState(false);

  // Select変更時
  const handleChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setRepeatType(value);
    setRepeatTypeForRestore(null);
    if (value === 'custom') {
      setRepeatTypeForRestore(repeatType);
      setOpenCustomDialog(true);
      return;
    }
    if (value === 'none') {
      handleSave({
        frequency: 'none',
        interval: 1,
        endCondition: 'none',
      });
    } else if (value === 'daily' || value === 'weekly' || value === 'monthly') {
      handleSave({
        frequency: value,
        interval: 1,
        endCondition: 'none',
      });
    } else if (value === 'weekdays') {
      handleSave({
        frequency: 'weekly',
        interval: 1,
        endCondition: 'none',
        dayOfWeeks: {
          mon: true,
          tue: true,
          wed: true,
          thu: true,
          fri: true,
          sat: false,
          sun: false,
        }
      });
    }
  };

  const handleClose = () => {
    setOpenCustomDialog(false);
    if (repeatTypeForRestore) {
      setRepeatType(repeatTypeForRestore);
    }
  };

  // カスタムダイアログで保存された内容を受け取る
  const handleSaveCustom = (rule: SalesOrderRecurrenceRule) => {
    handleSave(rule);
    setOpenCustomDialog(false);
    setRepeatTypeForRestore(null);
  };

  const onClickCustom = () => {
    setOpenCustomDialog(true);
  };

  const [currentCustom, setCurrentCustom] = useState<string>(null);

  useEffect(() => {
    if (!currentRule) return;

    let repeatTypeValue = 'none';
    if (currentRule.frequency === 'none') {
      repeatTypeValue = 'none';
    } else if (currentRule.frequency === 'daily') {
      if (currentRule.interval === 1 && currentRule.endCondition === 'none') {
        repeatTypeValue = 'daily';
      } else {
        repeatTypeValue = 'currentCustom';
      }
    } else if (currentRule.frequency === 'weekly') {
      if (currentRule.interval === 1
          && currentRule.endCondition === 'none'
          && isEqual(currentRule.dayOfWeeks, { mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false, })) {
        repeatTypeValue = 'weekdays';
      } else {
        repeatTypeValue = 'currentCustom';
      }
    } else if (currentRule.frequency === 'monthly') {
      repeatTypeValue = 'currentCustom';
    }
    setRepeatType(repeatTypeValue);
    if (repeatTypeValue === 'currentCustom') {
      setCurrentCustom(formatRecurrence(currentRule));
    }
  }, [currentRule]);

  return (
    <Box sx={{ width: 300 }}>
      <FormControl fullWidth>
        <InputLabel id="repeat-select-label">繰り返し設定</InputLabel>
        <Select
          labelId="repeat-select-label"
          id="repeat-select"
          value={repeatType}
          label="繰り返し設定"
          onChange={handleChange}
        >
          <MenuItem value="none">繰り返さない</MenuItem>
          <MenuItem value="daily">毎日</MenuItem>
          <MenuItem value="weekdays">平日 (月〜金)</MenuItem>
          {currentCustom && (
            <MenuItem value="currentCustom">
              {currentCustom}
            </MenuItem>
          )}
          <MenuItem value="custom" onClick={onClickCustom}>カスタム...</MenuItem>
        </Select>
      </FormControl>

      {/* カスタム設定用ダイアログ */}
      <CustomRepeatDialog
        currentRule={currentRule}
        open={openCustomDialog}
        onClose={handleClose}
        onSave={handleSaveCustom}
        // date={date}
      />
    </Box>
  );
});

export default RepeatSelect;
